import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { inject, observer } from 'mobx-react'
import {
  SendOutlined,
  SmileFilled,
  PictureFilled,
  CloseCircleFilled,
  PlayCircleFilled,
} from '@ant-design/icons'
import { Spin, message } from 'antd'
import CommentItem from './CommentItem'
import { getCaretCoordinates } from '../../../../utils/caret'
import { apiUrl } from '../../../../config'
import {
  CommentSectionContainer,
  CommentsList,
  CommentInputWrapper,
  UserAvatar,
  CommentInputBox,
  CommentActionBtn,
  colors,
} from '../../NewsFeedStyled'

/** Format name to Capitalize First Letters */
const formatName = name => {
  if (!name) return ''
  return name
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

const COMMON_EMOJIS = [
  '👍',
  '❤️',
  '😂',
  '😮',
  '😢',
  '😡',
  '🔥',
  '🎉',
  '👏',
  '🤔',
  '🙌',
  '✔️',
  '💯',
  '🚀',
  '💬',
  '🌸',
  '☀️',
  '🌟',
  '🤝',
  '😭',
]

const ACCEPTED_MEDIA = 'image/*,video/*'

/**
 * CommentSection - Khu vực comment dưới bài viết
 * - Load root comments (phân trang)
 * - Input viết comment kèm ảnh/video (tối đa 1 file)
 * - SSE real-time
 */
const CommentSection = ({
  postId,
  mediaId = null,
  currentUser,
  newsFeedStore,
  authenticationStore,
  userStore,
  compact = false,
  dark = false,
}) => {
  const [commentText, setCommentText] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [replyTo, setReplyTo] = useState(null) // { id, display_name }
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [attachedFile, setAttachedFile] = useState(null) // { file, previewUrl, type: 'image'|'video' }

  // Custom mention state
  const [mentionQuery, setMentionQuery] = useState('')
  const [showMentionList, setShowMentionList] = useState(false)
  const [mentionResults, setMentionResults] = useState([])
  const [mentionedUsers, setMentionedUsers] = useState([])
  const [selectedMentionIdx, setSelectedMentionIdx] = useState(0)
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 })

  const inputRef = useRef(null)
  const fileInputRef = useRef(null)
  const commentBackdropRef = useRef(null)

  // ── Derived state from store ────────────────────────────────────────────────
  const commentMapKey = newsFeedStore.getCommentMapKey(postId, mediaId)
  const commentState = newsFeedStore.commentsMap[commentMapKey]
  const comments = commentState?.comments || []
  const hasMore = commentState?.hasMore !== false
  const isLoading = commentState?.loading || false

  const { currentUserAvatar } = authenticationStore
  const avatarSrc = useMemo(
    () => (currentUserAvatar ? URL.createObjectURL(currentUserAvatar) : null),
    [currentUserAvatar]
  )

  const placeholder = useMemo(
    () =>
      replyTo ? `Trả lời ${replyTo.display_name}...` : 'Viết bình luận...',
    [replyTo]
  )

  const isSendable = useMemo(
    () => (commentText.trim().length > 0 || !!attachedFile) && !isSending,
    [commentText, attachedFile, isSending]
  )

  // ── Emoji picker style (stable object) ─────────────────────────────────────
  const emojiPickerStyle = useMemo(
    () => ({
      position: 'absolute',
      bottom: '100%',
      right: 0,
      background: '#ffffff',
      border: '1px solid #e2e8f0',
      borderRadius: 12,
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
      padding: 8,
      width: 210,
      zIndex: 1000,
      marginBottom: 8,
    }),
    []
  )

  const emojiGridStyle = useMemo(
    () => ({
      display: 'grid',
      gridTemplateColumns: 'repeat(5, 1fr)',
      gap: 6,
    }),
    []
  )

  // ── Effects ─────────────────────────────────────────────────────────────────
  // Close emoji picker on outside click
  useEffect(() => {
    if (!showEmojiPicker) return
    const handleOutsideClick = e => {
      if (!e.target.closest('.nf-comment-emoji-picker')) {
        setShowEmojiPicker(false)
      }
    }
    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [showEmojiPicker])

  // Load comments lần đầu + kết nối SSE
  useEffect(() => {
    newsFeedStore.getComments(postId, true, mediaId).catch(err => {
      console.error('Load comments error:', err)
    })
    newsFeedStore.connectSSE(postId)
    return () => {
      newsFeedStore.disconnectSSE(postId)
    }
  }, [postId, mediaId])

  // Revoke object URL khi unmount hoặc file đổi để tránh memory leak
  useEffect(() => {
    return () => {
      if (attachedFile?.previewUrl) {
        URL.revokeObjectURL(attachedFile.previewUrl)
      }
    }
  }, [attachedFile])

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const loadMoreComments = useCallback(() => {
    newsFeedStore.getComments(postId, false, mediaId).catch(err => {
      console.error('Load more comments error:', err)
    })
  }, [postId])

  // Backdrop highlighting — renders ALL text so textarea can be transparent
  const renderHighlightedComment = useCallback(
    text => {
      const textColor = dark ? '#e4e6eb' : '#050505'
      if (!text) return null
      if (mentionedUsers.length === 0) {
        return <span style={{ color: textColor }}>{text}</span>
      }

      const sortedUsers = [...mentionedUsers].sort(
        (a, b) => b.display.length - a.display.length
      )
      const escapeRegex = str => str.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')
      const namesRegexStr = sortedUsers
        .map(u => `@${escapeRegex(u.display)}`)
        .join('|')
      if (!namesRegexStr) return <span style={{ color: textColor }}>{text}</span>

      const regex = new RegExp(`(${namesRegexStr})`, 'g')
      const parts = text.split(regex)

      return parts.map((part, index) => {
        const matchedUser = sortedUsers.find(u => `@${u.display}` === part)
        if (matchedUser) {
          return (
            <span key={index}>
              <span style={{ color: 'transparent' }}>@</span>
              <span style={{
                color: '#1877f2',
                background: 'rgba(24, 119, 242, 0.12)',
                borderRadius: 3,
              }}>{matchedUser.display}</span>
            </span>
          )
        }
        return <span key={index} style={{ color: textColor }}>{part}</span>
      })
    },
    [mentionedUsers, dark]
  )

  // Handle typing inside comment textarea
  const handleCommentTextChange = useCallback(
    e => {
      const val = e.target.value
      setCommentText(val)

      // Clean up mentioned users no longer in text
      setMentionedUsers(prev => prev.filter(u => val.includes(`@${u.display}`)))

      // Detect mention trigger
      const cursorPos = e.target.selectionStart
      const textUpToCursor = val.slice(0, cursorPos)
      const atMatch = /@([\w]*)$/.exec(textUpToCursor)
      if (atMatch) {
        const query = atMatch[1]
        setMentionQuery(query)
        const results = (userStore.mentionUserList || [])
          .filter(u => u.display.toLowerCase().includes(query.toLowerCase()))
          .slice(0, 10)
        setMentionResults(results)
        setShowMentionList(true)
        setSelectedMentionIdx(0)

        // Calculate caret position for dropdown
        const atIndex = cursorPos - query.length - 1
        const coords = getCaretCoordinates(e.target, atIndex)
        setDropdownPos({
          top: coords.top + coords.height + 4,
          left: coords.left,
        })
      } else {
        setShowMentionList(false)
      }

      // Sync scroll with backdrop
      if (commentBackdropRef.current) {
        commentBackdropRef.current.scrollTop = e.target.scrollTop
      }
    },
    [userStore]
  )

  // Insert mention into comment textarea
  const insertMention = useCallback(
    user => {
      const input = inputRef.current
      if (!input) return
      const cursorPos = input.selectionStart
      const before = commentText.slice(0, cursorPos)
      const after = commentText.slice(cursorPos)
      const replaced = before.replace(/@[\w]*$/, `@${user.display} `)
      const newText = replaced + after
      setCommentText(newText)
      setMentionedUsers(prev => {
        if (prev.find(u => u.id === user.id)) return prev
        return [...prev, user]
      })
      setShowMentionList(false)
      setMentionQuery('')
      setTimeout(() => {
        input.focus()
        const newPos = replaced.length
        input.selectionStart = input.selectionEnd = newPos
        if (commentBackdropRef.current) {
          commentBackdropRef.current.scrollTop = input.scrollTop
        }
      }, 0)
    },
    [commentText, setMentionedUsers, setShowMentionList, setMentionQuery]
  )

  const insertEmoji = useCallback(
    emoji => {
      const input = inputRef.current
      if (input) {
        const start = input.selectionStart
        const end = input.selectionEnd
        const newText =
          commentText.substring(0, start) + emoji + commentText.substring(end)
        setCommentText(newText)
        setTimeout(() => {
          input.focus()
          input.selectionStart = input.selectionEnd = start + emoji.length
        }, 0)
      } else {
        setCommentText(prev => prev + emoji)
      }
      setShowEmojiPicker(false)
    },
    [commentText]
  )

  const toggleEmojiPicker = useCallback(() => {
    setShowEmojiPicker(prev => !prev)
  }, [])

  const handleFileChange = useCallback(e => {
    const file = e.target.files?.[0]
    if (!file) return

    const isImage = file.type.startsWith('image/')
    const isVideo = file.type.startsWith('video/')
    if (!isImage && !isVideo) {
      message.warning('Chỉ chấp nhận file ảnh hoặc video!')
      return
    }

    // Revoke old URL
    setAttachedFile(prev => {
      if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl)
      return null
    })

    const previewUrl = URL.createObjectURL(file)
    setAttachedFile({ file, previewUrl, type: isImage ? 'image' : 'video' })
    // Reset file input để có thể chọn lại cùng file
    e.target.value = ''
  }, [])

  const removeAttachedFile = useCallback(() => {
    setAttachedFile(prev => {
      if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl)
      return null
    })
  }, [])

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleSendComment = useCallback(async () => {
    const text = commentText.trim()
    if ((!text && !attachedFile) || isSending) return

    try {
      setIsSending(true)
      const mentionPrefix = replyTo ? `@[${replyTo.display_name}] ` : ''

      // Convert clean @Name to @[Name] for backend
      let formattedText = text
      const escapeRegex = str => str.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')
      mentionedUsers.forEach(u => {
        const regex = new RegExp(`@${escapeRegex(u.display)}`, 'g')
        formattedText = formattedText.replace(regex, `@[${u.display}]`)
      })

      const finalContent = mentionPrefix + formattedText

      let payload
      if (attachedFile) {
        // Dùng FormData để gửi file kèm text
        payload = new FormData()
        payload.append('content', finalContent)
        if (replyTo?.id) payload.append('parent_id', String(replyTo.id))
        if (mediaId != null) payload.append('media_id', String(mediaId))
        payload.append('file', attachedFile.file)
      } else {
        payload = {
          content: finalContent,
          parent_id: replyTo?.id || null,
          ...(mediaId != null ? { media_id: mediaId } : {}),
        }
      }

      await newsFeedStore.createComment(postId, payload)
      setCommentText('')
      setMentionedUsers([])
      setReplyTo(null)
      setAttachedFile(prev => {
        if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl)
        return null
      })
    } catch (err) {
      console.error('Send comment error:', err)
      message.error(err?.vi || 'Không thể gửi bình luận!')
    } finally {
      setIsSending(false)
    }
  }, [commentText, attachedFile, isSending, postId, mediaId, replyTo])

  const handleKeyDown = useCallback(
    e => {
      if (e.defaultPrevented) return
      if (showMentionList) {
        if (e.key === 'ArrowDown') {
          e.preventDefault()
          setSelectedMentionIdx(prev => (prev + 1) % mentionResults.length)
        } else if (e.key === 'ArrowUp') {
          e.preventDefault()
          setSelectedMentionIdx(
            prev => (prev - 1 + mentionResults.length) % mentionResults.length
          )
        } else if (e.key === 'Enter') {
          e.preventDefault()
          if (mentionResults[selectedMentionIdx]) {
            insertMention(mentionResults[selectedMentionIdx])
          }
        } else if (e.key === 'Escape') {
          e.preventDefault()
          setShowMentionList(false)
        }
        return
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSendComment()
      }
    },
    [
      handleSendComment,
      showMentionList,
      mentionResults,
      selectedMentionIdx,
      insertMention,
    ]
  )

  const moveCursorToEnd = inputEl => {
    if (!inputEl) return
    const len = inputEl.value?.length || 0
    // setTimeout để đợi react-mentions render xong rồi mới set
    setTimeout(() => {
      inputEl.focus()
      inputEl.setSelectionRange(len, len)
    }, 50)
  }

  const handleReply = useCallback(comment => {
    const targetId = comment.parent_id || comment.id
    setReplyTo({ id: targetId, display_name: formatName(comment.display_name) })
    moveCursorToEnd(inputRef.current)
  }, [])

  const cancelReply = useCallback(() => {
    setReplyTo(null)
  }, [])

  // ── Skeleton rows ─────────────────────────────────────────────────────────
  const skeletonRows = useMemo(
    () =>
      Array.from({ length: 2 }).map((_, idx) => (
        <div
          key={idx}
          className="nf-skeleton-pulse"
          style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: '#edeef1',
              flexShrink: 0,
            }}
          />
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}>
            <div
              style={{
                height: 28,
                background: '#edeef1',
                borderRadius: 14,
                width: '70%',
                maxWidth: 280,
              }}
            />
            <div style={{ display: 'flex', gap: 12, paddingLeft: 8 }}>
              <div
                style={{
                  height: 10,
                  background: '#edeef1',
                  borderRadius: 4,
                  width: 30,
                }}
              />
              <div
                style={{
                  height: 10,
                  background: '#edeef1',
                  borderRadius: 4,
                  width: 40,
                }}
              />
            </div>
          </div>
        </div>
      )),
    []
  )

  // ── Emoji items (stable array) ────────────────────────────────────────────
  const emojiItems = useMemo(
    () =>
      COMMON_EMOJIS.map(emoji => (
        <div
          key={emoji}
          onClick={() => insertEmoji(emoji)}
          style={{
            fontSize: 18,
            textAlign: 'center',
            cursor: 'pointer',
            padding: 4,
            borderRadius: 6,
            transition: 'background 0.1s',
            userSelect: 'none',
          }}
          onMouseEnter={e => (e.currentTarget.style.background = '#f1f5f9')}
          onMouseLeave={e =>
            (e.currentTarget.style.background = 'transparent')
          }>
          {emoji}
        </div>
      )),
    [insertEmoji]
  )

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <CommentSectionContainer
      className={dark ? 'nf-photo-comments-dark' : undefined}
      style={
        compact
          ? { padding: 0, border: 'none', background: 'transparent' }
          : undefined
      }>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_MEDIA}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {/* Comments List */}
      <CommentsList>
        {comments.map(comment => (
          <CommentItem
            key={comment.id}
            comment={comment}
            postId={postId}
            currentUser={currentUser}
            onReply={handleReply}
            depth={0}
            dark={dark}
          />
        ))}

        {/* Skeleton loading (first load) */}
        {isLoading && comments.length === 0 && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              padding: '8px 0',
            }}>
            {skeletonRows}
          </div>
        )}

        {/* Spinner (load more) */}
        {isLoading && comments.length > 0 && (
          <div style={{ textAlign: 'center', padding: 8 }}>
            <Spin size="small" />
          </div>
        )}

        {/* Load more */}
        {hasMore && !isLoading && comments.length > 0 && (
          <CommentActionBtn style={{ marginTop: 4 }} onClick={loadMoreComments}>
            Xem thêm bình luận
          </CommentActionBtn>
        )}
      </CommentsList>

      {/* Reply indicator */}
      {replyTo && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '6px 12px',
            background: dark ? '#3a3b3c' : colors.hoverBg,
            borderRadius: 8,
            fontSize: 13,
            color: dark ? '#b0b3b8' : colors.textSecondary,
          }}>
          <span>
            Đang trả lời <strong>{replyTo.display_name}</strong>
          </span>
          <button
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              color: colors.textMuted,
              fontSize: 14,
            }}
            onClick={cancelReply}>
            ✕
          </button>
        </div>
      )}

      {/* Attached file preview (nhỏ gọn) */}
      {attachedFile && (
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'flex-start',
            gap: 6,
            padding: '6px 10px',
            marginBottom: 4,
          }}>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            {attachedFile.type === 'image' ? (
              <img
                src={attachedFile.previewUrl}
                alt="preview"
                style={{
                  width: 72,
                  height: 72,
                  objectFit: 'cover',
                  borderRadius: 8,
                  border: '1px solid #e2e8f0',
                  display: 'block',
                }}
              />
            ) : (
              <div
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: 8,
                  background: '#1e293b',
                  border: '1px solid #e2e8f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                }}>
                <video
                  src={attachedFile.previewUrl}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                  muted
                />
                <PlayCircleFilled
                  style={{
                    color: '#fff',
                    fontSize: 24,
                    position: 'relative',
                    zIndex: 1,
                    opacity: 0.9,
                  }}
                />
              </div>
            )}
            {/* Remove button */}
            <CloseCircleFilled
              onClick={removeAttachedFile}
              style={{
                position: 'absolute',
                top: -6,
                right: -6,
                fontSize: 17,
                color: '#64748b',
                background: '#fff',
                borderRadius: '50%',
                cursor: 'pointer',
                boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
              }}
            />
          </div>
        </div>
      )}

      {/* Comment Input */}
      <CommentInputWrapper>
        <UserAvatar
          style={{ width: 32, height: 32, fontSize: 13, flexShrink: 0 }}>
          {avatarSrc ? (
            <img src={avatarSrc} alt="" />
          ) : (
            (currentUser?.name_uppercase || 'U').charAt(0)
          )}
        </UserAvatar>

        <CommentInputBox
          style={{
            position: 'relative',
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: 0,
            border: '1px solid transparent',
            borderRadius: 20,
            background: dark ? '#3a3b3c' : '#f0f2f5',
            padding: '6px 12px',
            flex: 1,
          }}
          onMouseEnter={e => {
            e.currentTarget.style.borderColor = dark ? '#4e4f50' : '#ccd0d5'
            e.currentTarget.style.background = dark ? '#4e4f50' : '#e4e6eb'
          }}
          onMouseLeave={e => {
            e.currentTarget.style.borderColor = 'transparent'
            e.currentTarget.style.background = dark ? '#3a3b3c' : '#f0f2f5'
          }}
          onFocusCapture={e => {
            e.currentTarget.style.borderColor = dark ? '#2c65ac' : '#ccd0d5'
            e.currentTarget.style.background = dark ? '#242526' : '#ffffff'
          }}
          onBlurCapture={e => {
            e.currentTarget.style.borderColor = 'transparent'
            e.currentTarget.style.background = dark ? '#3a3b3c' : '#f0f2f5'
          }}>
          {/* Emoji picker */}
          {showEmojiPicker && (
            <div className="nf-comment-emoji-picker" style={emojiPickerStyle}>
              <div style={emojiGridStyle}>{emojiItems}</div>
            </div>
          )}

          {/* Text input row */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              paddingRight: 8,
            }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <style>{`
                /* ── textarea: transparent so backdrop shows through ── */
                .nf-comment-textarea {
                  color: transparent !important;
                  caret-color: ${dark ? '#e4e6eb' : '#050505'} !important;
                  background: transparent !important;
                  resize: none !important;
                  border: none !important;
                  outline: none !important;
                  box-shadow: none !important;
                  font-family: inherit !important;
                  font-size: 14px !important;
                  line-height: 20px !important;
                  padding: 6px 0 2px 0 !important;
                  margin: 0 !important;
                  width: 100% !important;
                }
                .nf-comment-textarea::placeholder {
                  color: #b0b3b8 !important;
                }
                .nf-comment-textarea::selection {
                  background-color: #cce2ff !important;
                }
                .nf-comment-textarea:focus {
                  border: none !important;
                  outline: none !important;
                  box-shadow: none !important;
                }
              `}</style>

              {/* Highlight Backdrop */}
              <div
                ref={commentBackdropRef}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  padding: '6px 0 2px 0',
                  margin: 0,
                  fontFamily: 'inherit',
                  fontSize: '14px',
                  lineHeight: '20px',
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  pointerEvents: 'none',
                  overflow: 'hidden',
                  boxSizing: 'border-box',
                }}>
                {renderHighlightedComment(commentText)}
              </div>

              <textarea
                ref={inputRef}
                className="nf-comment-textarea"
                value={commentText}
                onChange={handleCommentTextChange}
                onScroll={e => {
                  if (commentBackdropRef.current) {
                    commentBackdropRef.current.scrollTop = e.target.scrollTop
                  }
                }}
                placeholder={placeholder}
                onKeyDown={handleKeyDown}
                disabled={isSending}
                maxLength={2000}
                rows={1}
                spellCheck={false}
                style={{
                  position: 'relative',
                  zIndex: 1,
                  minHeight: 20,
                  maxHeight: 300,
                  overflow: 'hidden',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
              />

              {/* Mention dropdown */}
              {showMentionList && mentionResults.length > 0 && (
                <div
                  className="mention-dropdown"
                  style={{
                    position: 'absolute',
                    left: dropdownPos.left,
                    top: dropdownPos.top,
                    background: '#fff',
                    border: '1px solid #e4e6eb',
                    borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    zIndex: 1000,
                    width: '300px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                  }}>
                  {mentionResults.map((u, idx) => (
                    <div
                      key={u.id}
                      onMouseDown={e => { e.preventDefault(); insertMention(u); }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '8px 12px',
                        background:
                          idx === selectedMentionIdx ? '#f2f3f5' : '#fff',
                        cursor: 'pointer',
                        transition: 'background 0.2s',
                        borderBottom: '1px solid #f1f5f9',
                      }}
                      onMouseEnter={() => setSelectedMentionIdx(idx)}>
                      {/* Avatar */}
                      {u.image ? (
                        <img
                          src={
                            u.image.startsWith('http') || u.image.startsWith('data:')
                              ? u.image
                              : `${apiUrl}/api/v1/images/${u.image}`
                          }
                          alt=""
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            objectFit: 'cover',
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            background: '#1877f2',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 11,
                            fontWeight: 'bold',
                          }}>
                          {u.display.charAt(0)}
                        </div>
                      )}
                      {/* Info */}
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          flex: 1,
                          overflow: 'hidden',
                        }}>
                        <span
                          style={{
                            fontWeight: 600,
                            fontSize: 13,
                            color: '#050505',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}>
                          {u.display}
                        </span>
                        <span
                          style={{
                            fontSize: 11,
                            color: '#65676b',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}>
                          @{u.username || u.id}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions group */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                marginLeft: 8,
              }}>
              {/* Attach media button */}
              <PictureFilled
                onClick={openFilePicker}
                title="Đính kèm ảnh/video"
                style={{
                  fontSize: 20,
                  lineHeight: 1,
                  color: attachedFile ? colors.primary : '#8c939d',
                  cursor: 'pointer',
                  transition: 'color 0.2s',
                  display: 'flex',
                }}
              />

              {/* Emoji button */}
              <SmileFilled
                onClick={toggleEmojiPicker}
                title="Chọn biểu tượng cảm xúc"
                style={{
                  fontSize: 20,
                  lineHeight: 1,
                  color: showEmojiPicker ? '#f59e0b' : '#8c939d',
                  cursor: 'pointer',
                  transition: 'color 0.2s',
                  display: 'flex',
                }}
              />

              {/* Send button */}
              {isSending ? (
                <Spin size="small" />
              ) : (
                <SendOutlined
                  onClick={isSendable ? handleSendComment : undefined}
                  style={{
                    fontSize: 20,
                    lineHeight: 1,
                    color: isSendable ? colors.primary : '#bcc0c4',
                    cursor: isSendable ? 'pointer' : 'default',
                    transition: 'color 0.2s',
                    display: 'flex',
                  }}
                />
              )}
            </div>
          </div>
        </CommentInputBox>
      </CommentInputWrapper>
    </CommentSectionContainer>
  )
}

export default inject(
  'newsFeedStore',
  'authenticationStore',
  'userStore'
)(observer(CommentSection))
